import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from '../data.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface WebshopPageDefinition {
  key: string;
  label: string;
  description: string;
  icon: string;
  path: string;
  parent_key?: string;
}

interface WebshopThemeSettings {
  name: string;
  primary_color: string;
  accent_color: string;
  background_color: string;
  paper_color: string;
  text_color: string;
  dark_mode: number;
  simplepay_enabled: number;
  simplepay_sandbox: number;
  simplepay_sandbox_merchant: string;
  simplepay_sandbox_secret: string;
  simplepay_live_merchant: string;
  simplepay_live_secret: string;
  simplepay_has_sandbox_secret?: boolean;
  simplepay_has_live_secret?: boolean;
  active: number;
}

@Component({
  selector: 'app-webshop-admin',
  templateUrl: './webshop-admin.component.html',
  styleUrls: ['./webshop-admin.component.scss'],
})
export class WebshopAdminComponent implements OnInit {
  readonly fallbackPages: WebshopPageDefinition[] = [
    { key: 'home', label: 'Főoldal', description: 'Nyitóoldali banner és kiemelt blokkok', icon: 'home-outline', path: 'home' },
    { key: 'termekek', label: 'Termékek', description: 'Terméklista és kategória nézet', icon: 'cube-outline', path: 'termekek' },
    { key: 'tovabbitermekek', label: 'További termékek', description: 'Másodlagos katalógus oldal', icon: 'albums-outline', path: 'tovabbitermekek' },
    { key: 'kapcsolat', label: 'Kapcsolat', description: 'Kapcsolati információk', icon: 'mail-outline', path: 'kapcsolat' },
    { key: 'rolunk', label: 'Rólunk', description: 'Bemutatkozó tartalom', icon: 'sparkles-outline', path: 'rolunk' },
    { key: 'ajandekkartya', label: 'Ajándékkártya', description: 'Ajándékkártya landing oldal', icon: 'gift-outline', path: 'ajandekkartya' },
  ];

  sajatIntezmeny: any;
  config: any;
  pages: any[] = [];
  sections: any[] = [];
  themeSettings: WebshopThemeSettings = {
    name: 'Webshop',
    primary_color: '#12413b',
    accent_color: '#d6853d',
    background_color: '#fffaf0',
    paper_color: '#fffaf0',
    text_color: '#19231f',
    dark_mode: 0,
    simplepay_enabled: 0,
    simplepay_sandbox: 1,
    simplepay_sandbox_merchant: '',
    simplepay_sandbox_secret: '',
    simplepay_live_merchant: '',
    simplepay_live_secret: '',
    active: 1,
  };
  selectedPage = 'home';
  loading = true;
  saving = false;
  error = '';
  uploadingImageKey = '';

  constructor(
    private http: HttpClient,
    private data: DataService,
  ) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.loadConfig();
  }

  loadConfig() {
    this.loading = true;
    this.error = '';

    this.http.get(`/api/webshop/config/${this.tenantCode}`).subscribe({
      next: (response: any) => {
        this.config = response;
        this.applyThemeFromConfig(response);
        this.loadSettings();
        this.loadPages();
      },
      error: () => {
        this.error = 'A webshop API jelenleg nem elérhető.';
        this.loading = false;
      },
    });
  }

  loadSettings() {
    this.http.get<any>(`/api/webshop/admin/settings/${this.tenantCode}`).subscribe({
      next: (response) => {
        if (response?.settings) {
          this.themeSettings = {
            ...this.themeSettings,
            ...response.settings,
            dark_mode: Number(response.settings.dark_mode ?? 0),
            simplepay_enabled: Number(response.settings.simplepay_enabled ?? 0),
            simplepay_sandbox: Number(response.settings.simplepay_sandbox ?? 1),
            simplepay_sandbox_secret: '',
            simplepay_live_secret: '',
            simplepay_has_sandbox_secret: !!response.settings.simplepay_has_sandbox_secret,
            simplepay_has_live_secret: !!response.settings.simplepay_has_live_secret,
            active: Number(response.settings.active ?? 1),
          };
        }
      },
      error: () => {
        // The public config already provided safe defaults, so the editor can still work.
      },
    });
  }

  applyThemeFromConfig(response: any) {
    this.themeSettings = {
      name: response?.name || 'Webshop',
      primary_color: response?.theme?.primary || '#12413b',
      accent_color: response?.theme?.accent || '#d6853d',
      background_color: response?.theme?.background || '#fffaf0',
      paper_color: response?.theme?.paper || '#fffaf0',
      text_color: response?.theme?.text || '#19231f',
      dark_mode: response?.theme?.darkMode ? 1 : 0,
      simplepay_enabled: response?.payment?.simplepayEnabled ? 1 : 0,
      simplepay_sandbox: response?.payment?.simplepaySandbox === false ? 0 : 1,
      simplepay_sandbox_merchant: '',
      simplepay_sandbox_secret: '',
      simplepay_live_merchant: '',
      simplepay_live_secret: '',
      active: 1,
    };
  }

  saveSettingsOnly() {
    return this.http.put(`/api/webshop/admin/settings/${this.tenantCode}`, this.normalizeThemeSettings());
  }

  normalizeThemeSettings(): WebshopThemeSettings {
    return {
      ...this.themeSettings,
      dark_mode: Number(this.themeSettings.dark_mode ? 1 : 0),
      simplepay_enabled: Number(this.themeSettings.simplepay_enabled ? 1 : 0),
      simplepay_sandbox: Number(this.themeSettings.simplepay_sandbox ? 1 : 0),
      active: Number(this.themeSettings.active ?? 1),
    };
  }

  loadPages() {
    this.http.get<any>(`/api/webshop/admin/pages/${this.tenantCode}`).subscribe({
      next: (response) => {
        this.pages = Array.isArray(response?.pages)
          ? response.pages.map((page: any, index: number) => ({
            ...page,
            key: page.key || page.page_key || page.path,
            page_key: page.page_key || page.key || page.path,
            parent_key: page.parent_key || page.parentKey || '',
            sort_order: Number(page.sort_order ?? ((index + 1) * 10)),
            active: Number(page.active ?? 1),
          }))
          : this.fallbackPages.map((page, index) => ({ ...page, page_key: page.key, parent_key: '', sort_order: (index + 1) * 10, active: 1 }));
        this.pages.sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
        this.normalizePageParents();
        this.sections = Array.isArray(response?.sections)
          ? response.sections.map((section: any) => this.normalizeSection(section))
          : [];
        if (!this.pages.some((page) => page.key === this.selectedPage)) {
          this.selectedPage = this.pages[0]?.key || 'home';
        }
        this.loading = false;
      },
      error: () => {
        this.error = 'Az oldalszerkesztő adatai nem tölthetők be.';
        this.loading = false;
      },
    });
  }

  savePages() {
    this.saving = true;
    this.normalizePageOrders();
    this.http.put(`/api/webshop/admin/pages/${this.tenantCode}`, { pages: this.pages, sections: this.sections }).subscribe({
      next: () => {
        this.saving = false;
        this.data.sendToast('A webshop oldal tartalma mentve.');
      },
      error: () => {
        this.saving = false;
        this.data.sendToast('Nem sikerült menteni a webshop oldalt.', 'error');
      },
    });
  }

  saveAll() {
    this.saving = true;
    this.normalizePageOrders();

    this.saveSettingsOnly().subscribe({
      next: () => {
        this.http.put(`/api/webshop/admin/pages/${this.tenantCode}`, { pages: this.pages, sections: this.sections }).subscribe({
          next: () => {
            this.saving = false;
            this.data.sendToast('A webshop beállításai és oldalai mentve.');
          },
          error: () => {
            this.saving = false;
            this.data.sendToast('A színek mentve, de az oldaltartalom mentése nem sikerült.', 'error');
          },
        });
      },
      error: () => {
        this.saving = false;
        this.data.sendToast('Nem sikerült menteni a webshop megjelenését.', 'error');
      },
    });
  }

  get tenantCode(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.om || 'demo').trim();
  }

  get previewUrl(): string {
    return `/api/webshop/${this.tenantCode}/${this.currentPage?.path || this.selectedPage}`;
  }

  get currentPage(): WebshopPageDefinition | undefined {
    return this.pageTabs.find((page) => page.key === this.selectedPage);
  }

  get selectedPageModel(): any {
    return this.pages.find((page) => (page.key || page.page_key) === this.selectedPage);
  }

  get pageTabs(): WebshopPageDefinition[] {
    return [...this.pages]
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
      .map((page) => ({
        key: page.key || page.page_key,
        label: page.label || page.key || page.page_key,
        description: page.description || 'Egyedi webshop oldal',
        icon: page.icon || 'document-text-outline',
        path: page.path || page.key || page.page_key,
        parent_key: page.parent_key || '',
      }));
  }

  get visibleSections(): any[] {
    return this.sections
      .filter((section) => section.page_key === this.selectedPage)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  }

  get activeSectionsCount(): number {
    return this.visibleSections.filter((section) => this.isSectionActive(section)).length;
  }

  selectPage(page: WebshopPageDefinition) {
    this.selectedPage = page.key;
  }

  addPage() {
    const pageNo = this.pages.length + 1;
    const pageKey = `uj-oldal-${Date.now()}`;
    this.pages.push({
      key: pageKey,
      page_key: pageKey,
      label: `Új oldal ${pageNo}`,
      description: 'Egyedi webshop oldal',
      icon: 'document-text-outline',
      path: pageKey,
      parent_key: '',
      sort_order: this.pages.length * 10 + 10,
      active: 1,
    });
    this.selectedPage = pageKey;
  }

  deleteCurrentPage() {
    const page = this.currentPage;
    if (!page) return;
    if (!window.confirm(`Biztosan törlöd ezt az oldalt: ${page.label}?`)) return;

    this.pages = this.pages.filter((item) => (item.key || item.page_key) !== page.key);
    this.sections = this.sections.filter((section) => section.page_key !== page.key);
    this.selectedPage = this.pageTabs[0]?.key || 'home';
    this.normalizePageOrders();
    this.savePages();
  }

  updateCurrentPageKey() {
    const page = this.pages.find((item) => (item.key || item.page_key) === this.selectedPage);
    if (!page) return;

    const oldKey = this.selectedPage;
    const newKey = this.slugify(page.key || page.page_key || page.label);
    page.key = newKey;
    page.page_key = newKey;
    page.path = this.slugify(page.path || newKey);
    this.sections
      .filter((section) => section.page_key === oldKey)
      .forEach((section) => {
        section.page_key = newKey;
      });
    this.pages
      .filter((item) => item.parent_key === oldKey)
      .forEach((item) => {
        item.parent_key = newKey;
      });
    this.selectedPage = newKey;
  }

  dropPage(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.pages, event.previousIndex, event.currentIndex);
    this.normalizePageOrders();
  }

  normalizePageOrders() {
    this.pages.forEach((page, index) => {
      page.sort_order = (index + 1) * 10;
      page.key = this.slugify(page.key || page.page_key || page.label);
      page.page_key = page.key;
      page.path = this.slugify(page.path || page.key);
      page.icon = page.icon || 'document-text-outline';
      page.parent_key = this.slugifyOptional(page.parent_key);
      if (page.parent_key === page.key) page.parent_key = '';
      page.active = Number(page.active ?? 1);
    });
    this.normalizePageParents();
  }

  normalizePageParents() {
    const pageKeys = new Set(this.pages.map((page) => page.key || page.page_key).filter(Boolean));
    this.pages.forEach((page) => {
      if (!page.parent_key || page.parent_key === page.key || !pageKeys.has(page.parent_key)) {
        page.parent_key = '';
      }
    });
  }

  getParentPageOptions(page: any): any[] {
    const currentKey = page?.key || page?.page_key;
    return this.pages
      .filter((item) => {
        const key = item.key || item.page_key;
        return key && key !== currentKey;
      })
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  }

  countPageActiveSections(pageKey: string): number {
    return this.sections.filter((section) => section.page_key === pageKey && this.isSectionActive(section)).length;
  }

  isSectionActive(section: any): boolean {
    return Number(section?.active ?? 1) === 1;
  }

  toggleSection(section: any) {
    section.active = this.isSectionActive(section) ? 0 : 1;
  }

  addSection() {
    this.sections.push({
      page_key: this.selectedPage,
      section_key: `uj-blokk-${Date.now()}`,
      title: '',
      subtitle: '',
      body: '',
      button_label: '',
      button_route: '',
      image_url: '',
      extra_json: { image_background: false, background_gradient: '' },
      sort_order: this.visibleSections.length * 10 + 100,
      active: 1,
    });
  }

  duplicateSection(section: any) {
    this.sections.push({
      ...section,
      id: undefined,
      extra_json: { ...(section.extra_json || {}) },
      section_key: `${section.section_key || 'blokk'}-masolat-${Date.now()}`,
      sort_order: this.visibleSections.length * 10 + 100,
    });
  }

  uploadSectionImage(event: Event, section: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.data.sendToast('Csak kép fájl tölthető fel.', 'error');
      input.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target', `page-${section.page_key || this.selectedPage}`);
    this.uploadingImageKey = `section-${section.page_key}-${section.section_key}`;

    this.http.post<any>(`/api/webshop/admin/catalog-upload/${this.tenantCode}`, formData).subscribe({
      next: (response) => {
        section.image_url = response?.url || response?.path || '';
        this.uploadingImageKey = '';
        input.value = '';
        this.data.sendToast('A blokk képe feltöltve, ne felejtsd el menteni az oldalt.');
      },
      error: () => {
        this.uploadingImageKey = '';
        input.value = '';
        this.data.sendToast('Nem sikerült feltölteni a blokk képét.', 'error');
      },
    });
  }

  imagePreviewUrl(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('/')) return raw;
    return `/api/file/${raw}`;
  }

  isUploadingSectionImage(section: any): boolean {
    return this.uploadingImageKey === `section-${section.page_key}-${section.section_key}`;
  }

  ensureSectionExtra(section: any) {
    if (!section.extra_json || typeof section.extra_json !== 'object') {
      section.extra_json = {};
    }
    if (section.extra_json.image_background === undefined) {
      section.extra_json.image_background = false;
    }
    if (section.extra_json.background_gradient === undefined) {
      section.extra_json.background_gradient = '';
    }
    if (section.section_key === 'categories' && section.page_key === 'home' && section.extra_json.show_all_products_category === undefined) {
      section.extra_json.show_all_products_category = 1;
    }
    return section.extra_json;
  }

  removeSection(section: any) {
    section.active = 0;
  }

  dropSection(event: CdkDragDrop<any[]>) {
    const pageSections = this.visibleSections;
    moveItemInArray(pageSections, event.previousIndex, event.currentIndex);
    pageSections.forEach((section, index) => {
      section.sort_order = (index + 1) * 10;
    });
  }

  private slugify(value: string): string {
    return String(value || 'oldal')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'oldal';
  }

  private slugifyOptional(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return this.slugify(raw);
  }

  private normalizeSection(section: any): any {
    const normalized = { ...section };
    if (typeof normalized.extra_json === 'string') {
      try {
        normalized.extra_json = JSON.parse(normalized.extra_json || '{}');
      } catch {
        normalized.extra_json = {};
      }
    }
    this.ensureSectionExtra(normalized);
    return normalized;
  }
}
